import { Raycaster, Vector2, Camera, Scene, Object3D, Group } from 'three';

export type PickResult = {
  type: 'agent' | 'task';
  id: string;
  object: Object3D;
} | null;

export class InputManager {
  private raycaster = new Raycaster();
  private mouse = new Vector2();
  private camera: Camera;
  private agentsGroup: Group;
  private taskQueueGroup: Group;
  private onPick: (result: PickResult) => void;
  private onHover: (result: PickResult) => void;
  private canvas: HTMLCanvasElement;

  constructor(
    canvas: HTMLCanvasElement,
    camera: Camera,
    agentsGroup: Group,
    taskQueueGroup: Group,
    onPick: (result: PickResult) => void,
    onHover: (result: PickResult) => void,
  ) {
    this.canvas = canvas;
    this.camera = camera;
    this.agentsGroup = agentsGroup;
    this.taskQueueGroup = taskQueueGroup;
    this.onPick = onPick;
    this.onHover = onHover;

    canvas.addEventListener('pointerdown', this.handlePointerDown);
    canvas.addEventListener('pointermove', this.handlePointerMove);
  }

  private updateMouse(event: PointerEvent): void {
    const rect = this.canvas.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  }

  private raycast(): PickResult {
    this.raycaster.setFromCamera(this.mouse, this.camera);

    // Check task cards first (higher priority for dispatch interaction)
    const taskHits = this.raycaster.intersectObjects(this.taskQueueGroup.children, true);
    if (taskHits.length > 0) {
      const taskGroup = this.findParentWithUserData(taskHits[0].object, 'taskId');
      if (taskGroup) {
        return {
          type: 'task',
          id: taskGroup.userData.taskId,
          object: taskGroup,
        };
      }
    }

    // Check agents
    const agentHits = this.raycaster.intersectObjects(this.agentsGroup.children, true);
    if (agentHits.length > 0) {
      const agentGroup = this.findParentWithUserData(agentHits[0].object, 'agentId');
      if (agentGroup) {
        return {
          type: 'agent',
          id: agentGroup.userData.agentId,
          object: agentGroup,
        };
      }
    }

    return null;
  }

  private findParentWithUserData(obj: Object3D, key: string): Object3D | null {
    let current: Object3D | null = obj;
    while (current) {
      if (current.userData && current.userData[key]) return current;
      current = current.parent;
    }
    return null;
  }

  private handlePointerDown = (event: PointerEvent): void => {
    this.updateMouse(event);
    const result = this.raycast();
    this.onPick(result);
  };

  private handlePointerMove = (event: PointerEvent): void => {
    this.updateMouse(event);
    const result = this.raycast();
    this.onHover(result);
  };

  dispose(): void {
    this.canvas.removeEventListener('pointerdown', this.handlePointerDown);
    this.canvas.removeEventListener('pointermove', this.handlePointerMove);
  }
}
